// ОБОЛОЧКА ВИЗАРДА — три полосы хрома и ряд футер-навигации.
//
// Что здесь есть: счётчик `NN / 07`, тело шага и ряд футера по таблице `footerNav.js` — пара
// `← back` + CTA на шагах 2–6 и пара `continue` + ghost `start over` в состоянии входа (B3-4,
// `s863.d1` / `s895.d1`). Состав ряда оболочка НЕ выбирает: она печатает то, что дала таблица.
//
// ⭐ ЧТО ДОБАВИЛ B4-4 (`3e419875cbb881abb94bdf344ee1d2c5`) — ДВЕ ВЕЩИ, И ОБЕ ЧУЖИЕ ПО СОДЕРЖАНИЮ:
// гейт-состояние CTA (надпись и запрет приходят ОТ ШАГА через канал провайдера) и тост перехода
// (строку приносит шаг, а печатает оболочка — он обязан пережить смену экрана). Ни одного слова
// шага 2 в этом файле нет: оболочка печатает то, что ей отдали, и только.
// Чего здесь нет: самих экранов s1–s7 — у каждого свой тикет. Оболочка принимает `renderStep`,
// а без него показывает заглушку с адресом: приёмка 2 требует, чтобы семь ПУСТЫХ шагов
// проходились вперёд и назад, и заглушка — честное имя этого состояния.
//
// ВЫСОТА И ЛИПКОСТЬ — хром §1–§2 (`docs/design/wizard-browser-rules.md`): корень `100dvh`
// (не `100vh`), тело — единственная сжимаемая полоса (`flex-1 min-h-0`), футер прибит к низу с
// `env(safe-area-inset-bottom)`, на корне `overscroll-behavior-y: contain` против
// pull-to-refresh (§5). ⚠️ Классы здесь СТРУКТУРНЫЕ. Вид — борды CD (Stage Gate v5 / Date
// Control v2 / Insertion Edges), и проверяется он глазом и playwright: в jsdom нет ни метрик
// шрифта, ни вьюпорта, поэтому «футер липкий» здесь машинно не доказуемо. Машинно проверяется
// другое — что ряда нет там, где его быть не должно.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import DebugExport, { isDebugSearch } from './DebugExport.jsx';
import { useWizard } from './WizardProvider.jsx';

/** Заглушка шага: печатает адрес, чтобы «пустой шаг» был различим, а не пустым экраном. */
function StepPlaceholder({ address }) {
  return (
    <p data-testid="step-placeholder" className="text-[var(--paper-faint)]">
      {/* [TODO: UI text — ask Kirill/web-Claude] — тело шага придёт со своим тикетом. */}
      {address}
    </p>
  );
}

/** Ход приглушённая → яркая на естественной посадке reveal, мс (`s977.d2` п. 3; `.fade` в `wizard-s1.css`). */
export const FADE_MS = 480;
/** Сколько держится `.cut` (без перехода) после смены вида кнопки, мс. */
export const CUT_HOLD_MS = 50;

export default function WizardShell({ renderStep }) {
  const {
    session, view, counter, footer, gate, ctaShake, pressCta, pressBack, toast, startOver,
  } = useWizard();
  const row = footer.back || footer.cta || footer.restart;
  // `?debug` читается один раз на монтировании: роутер меняет только хэш, строка запроса живёт.
  const [debug] = useState(() => typeof window !== 'undefined' && isDebugSearch(window.location.search));

  /**
   * Отказ гейта виден кнопкой (борд: `.cta.shake` в `$('cta').onclick`).
   *
   * ⚠️ АНИМАЦИЯ ПЕРЕЗАПУСКАЕТСЯ ЧЕРЕЗ DOM, А НЕ ЧЕРЕЗ `key`, И ЭТО ВЫБОР, А НЕ СПЕШКА. `key`
   * пересоздал бы узел — на бейдже шага 2 так и сделано, и там это верно (бейдж никто не
   * держит). Здесь узел — кнопка футера: пересоздание отнимает фокус у того, кто нажал её
   * клавиатурой, то есть наказывает за способ ввода. `classList` + reflow — ровно то, что
   * делает борд (`void b.offsetWidth`), и фокус остаётся на месте.
   */
  const ctaRef = useRef(null);
  const shake = () => {
    const node = ctaRef.current;
    if (!node) return;
    node.classList.remove('shake');
    void node.offsetWidth;
    node.classList.add('shake');
  };

  /**
   * ⭐ ТОТ ЖЕ ОТКАЗ, НО НАЧАТЫЙ НЕ КНОПКОЙ (B5-3). Тап по запертой точке панели деки обязан
   * тряхнуть эту же кнопку; повод приходит счётчиком из провайдера. Ноль пропускается намеренно —
   * иначе кнопка тряслась бы на первом же монтировании.
   */
  useEffect(() => {
    if (ctaShake) shake();
  }, [ctaShake]);

  /**
   * ⭐ СМЕНА ВИДА КНОПКИ — СРАЗУ, КРОМЕ ЕСТЕСТВЕННОЙ ПОСАДКИ (B6-7, `s977.d2` п. 3–4).
   * Общий `.wz-cta` несёт `transition: all .18s`, и без вмешательства любая смена вида (контур →
   * сплошная на входе в шаг 5, приглушённая → яркая по `skip`) шла бы 180 мс — на повторном входе
   * это было видно как мигание контура (замер CC 08.10). Поэтому на кадр смены оболочка ставит
   * `.cut` (перехода нет), а на выходе из приглушения по естественной посадке — `.fade` (480 мс,
   * ease-out). Ставится в layout-эффекте: до отрисовки, то есть браузер ни разу не видит
   * промежуточный вид. Через DOM, как `shake`, и по той же причине: узел пересоздавать нельзя.
   * Снимается сам: `.cut` — через `CUT_HOLD_MS` (наведение возвращает свои .18s), `.fade` — по концу хода.
   */
  const look = `${gate?.tone ?? ''}|${gate?.muted ? 'muted' : ''}`;
  const lookRef = useRef(null);
  useLayoutEffect(() => {
    const node = ctaRef.current;
    const prev = lookRef.current;
    lookRef.current = look;
    if (!node || prev === null || prev === look) return;
    const fade = prev.endsWith('|muted') && !gate?.muted && !!gate?.fade;
    node.classList.remove('cut', 'fade');
    node.classList.add(fade ? 'fade' : 'cut');
    if (fade) {
      setTimeout(() => node.classList.remove('fade'), FADE_MS);
    } else {
      // Таймер, а не два `requestAnimationFrame`: вложенные кадры ломали ожидание кадра в тестах
      // проводки шага 5 под поддельными таймерами (`StepFive.cut.test.jsx`). 50 мс — заведомо после
      // применения нового вида; наведение получает свои .18s обратно.
      setTimeout(() => node.classList.remove('cut'), CUT_HOLD_MS);
    }
  }, [look, gate?.muted, gate?.fade]);

  return (
    <div
      data-testid="wizard-shell"
      // `relative` — опора тоста перехода: он `position:absolute` байтом борда и обязан
      // отмеряться от низа СТЕКЛА, как у борда от низа рамки. Тело оболочки для него не подходит:
      // оно кончается над футером, а борд рисует тост поверх ряда CTA.
      className="relative flex h-[100dvh] min-h-0 flex-col overscroll-y-contain bg-[var(--ink-900)] text-[var(--paper)]"
    >
      <header className="shrink-0 px-6 pt-6">
        {/* СТАТУС-БАР ПО БОРДУ: `step` приглушён, число — бумажное и полужирное
            (`.status` + `.status b`). Слово `step` стояло в борде с 14.08, а печатали мы одно
            число — расхождение в КОПИ, не в числе; приведено к борду решением web-Claude.
            ⚠️ `data-testid="step-counter"` намеренно остался НА ЧИСЛЕ: счётчик — это `NN / 07`,
            а `step` его подпись. Сверку всей строки против байта борда держит
            `__parity__/wizard-s1-copy.test.jsx`, поэтому выбор тестида ничего не прячет. */}
        {/* ⭐ ЯНТАРЬ НА ЧИСЛЕ — ЕДИНСТВЕННОЕ ОТЛИЧИЕ ШАПКИ В СОСТОЯНИИ 2 (борд: `b.on`).
            Признак берётся из футера, а не из `entry`: амбер и пара `continue` + `start over`
            — один и тот же факт «человек на входе, работа есть», и разойтись они не имеют
            права. Бумажный цвет в этом состоянии НЕ ставится вовсе — иначе два класса одной
            специфичности спорили бы за цвет, и победил бы порядок файлов, а не решение. */}
        <p
          data-testid="step-status"
          className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--paper-mute)]"
        >
          step{' '}
          <b
            data-testid="step-counter"
            data-state={footer.restart ? 'resume' : 'plain'}
            className={`font-medium ${footer.restart ? 'wz-status-on' : 'text-[var(--paper)]'}`}
          >
            {counter}
          </b>
        </p>
      </header>

      {/* ⚠️ ЗАГЛУШКА — ФОЛБЭК И НА `null` ОТ `renderStep`, а не только на отсутствие пропа.
          Экраны приходят по одному (B3-2 дал s1, остальные шесть — свои тикеты), и проводка
          возвращает `null` на всё, чего ещё нет. Без этого `??` шаги 2–7 стали бы пустым
          телом: приёмка 2 «семь пустых шагов вперёд и назад» зеленела бы, а смотреть в
          браузере стало бы не на что. */}
      {/* `overflow-x-hidden` — как у `.screen` борда (`overflow-y:auto;overflow-x:hidden`).
          Не косметика: подсветка шага 1 намеренно вылезает за края на 15% с каждой стороны,
          и без этого браузер дал бы телу горизонтальную прокрутку. */}
      {/* ⚠️ ПЕЧАТАЕТСЯ `view`, А НЕ `session.step`. В состоянии входа (`s895.d1`) конверт помнит
          шаг остановки, а на экране стоит ЛЕНДИНГ: человек ещё не нажал `continue`. Различие
          сводит провайдер, оболочка его только исполняет — второй истины о позиции тут нет. */}
      <main data-testid="step-body" className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6">
        {renderStep?.(session, view) ?? <StepPlaceholder address={view} />}
      </main>

      {row ? (
        // ДВА ОБЪЕКТА ФУТЕРА ИДУТ СТОЛБЦОМ (борд: `.cta-row{flex-direction:column;gap:8px}`),
        // а пара `← back` + CTA — в ряд (ОВ-CC.1). Это разные объекты, а не одна раскладка с
        // вариантом: `justify-between` поставил бы `start over` СПРАВА ОТ `continue`, то есть
        // сделал бы из тихого выхода вторую кнопку той же весовой категории.
        <footer
          data-testid="footer-nav"
          className={`sticky bottom-0 shrink-0 flex px-6 pb-[env(safe-area-inset-bottom)] pt-4 ${
            footer.restart ? 'flex-col gap-2' : 'items-center justify-between gap-4'
          }`}
        >
          {/* ⭐ НАДПИСЬ ВОЗВРАТА ТОЖЕ МОЖЕТ ПРИЙТИ ОТ ШАГА (B5-2) — той же парой «надпись +
              обработчик», что и CTA. У шагов 1–2 её нет, и тогда печатается `← back` из
              таблицы: фолбэк ровно такой же, как у надписи CTA строкой ниже, и по той же
              причине — оболочка знает, что надпись МОЖЕТ прийти, а не какая она. */}
          {footer.back ? (
            <button type="button" data-testid="nav-back" onClick={() => { pressBack(); }}>
              {gate?.backLabel ?? footer.backLabel}
            </button>
          ) : null}
          {/* Словарь `.cta` борда целиком — поведение и вид, включая стрелку псевдоэлементом
              (`src/styles/wizard-s1.css`). Стрелка приходит на ВСЕ состояния, как у борда:
              его `begin` читается как `begin →`, его `continue` — как `continue →`. */}
          {/* CTA на входе ведёт НА СОХРАНЁННЫЙ ШАГ (`resume`), а не на следующий: `continue`
              состояния 2 — это возврат, а не движение вперёд. На всех прочих экранах это
              по-прежнему `next`. Обе ветки теперь внутри `pressCta` (B4-4): выбор между ними
              не менялся, он просто уехал туда, где у него появился третий сосед — вопрос к
              экрану, пускать ли вообще. */}
          {/* ⭐ ГЕЙТ ПЕЧАТАЕТ ЭКРАН, А НЕ ОБОЛОЧКА (B4-4, `s461`). Оболочка знает ровно две вещи:
              что кнопка может быть закрыта и что надпись тогда приходит от шага. Сам гейт — три
              ветки `cta()` борда шага 2 — живёт в `steps/stageGate.js`, и здесь его нет ни
              строкой: правило шага в чужой полосе стало бы вторым домом правила.
              ⚠️ ЧИСЛО ИДЁТ ОТДЕЛЬНЫМ УЗЛОМ (`.n`), как у борда: в «add 2 more to continue»
              громко ровно оно (`.cta.off .n{color:var(--amber);font-weight:700}`), и склеенная
              строка это потеряла бы.
              ⚠️ `aria-disabled`, А НЕ `disabled`: закрытая кнопка обязана ОТВЕТИТЬ на нажатие —
              тряхнуть собой и мигнуть кнопкой `add`. `disabled` съел бы событие, и экран,
              который объясняет себя, промолчал бы ровно там, где он нужнее всего. */}
          {footer.cta ? (
            <button
              ref={ctaRef}
              type="button"
              data-testid="nav-cta"
              aria-disabled={gate?.blocked ? 'true' : undefined}
              onClick={() => { if (pressCta() === false) shake(); }}
              className={`wz-cta${gate?.blocked ? ' off' : ''}${gate?.tone === 'solid' ? ' solid' : ''}${gate?.muted ? ' muted' : ''}`}
            >
              {/* ⚠️ ПОРЯДОК ВЕТОК ИСПРАВЛЕН В B5-3, И ЭТО БЫЛА НАСТОЯЩАЯ ПОЛОМКА, А НЕ ВКУС.
                  Здесь стояло «нет надписи → печатаем табличную», и проверялось это РАНЬШЕ числа.
                  Шаг 2 от такого порядка не страдал: его закрытый гейт кладёт и целую строку, и
                  её половинки. Шаг 3 половинки кладёт, а целую строку НЕТ — намеренно, чтобы одна
                  копи не жила в двух местах (`deckNav.js`), — и кнопка печатала `continue` вместо
                  `move 1 more to continue`. Строка была написана в B5-1, проверена в B5-2 и всё
                  равно не доезжала: ЧИСЛО СИЛЬНЕЕ НАДПИСИ, потому что оно и есть надпись. */}
              {gate?.count === null || gate?.count === undefined
                ? (gate?.label ?? footer.ctaLabel)
                : <>{gate.lead}<span className="n">{gate.count}</span>{gate.trail}</>}
            </button>
          ) : null}
          {/* Ghost-ссылка `start over` — шрифт меты, без рамки и без стрелки: продолжить это
              обычный ход, начать заново редкий, веса не равны (борд, `.cta.ghostlink`). */}
          {footer.restart ? (
            <button
              type="button"
              data-testid="nav-restart"
              onClick={() => startOver()}
              className="wz-cta wz-ghostlink"
            >
              {footer.restartLabel}
            </button>
          ) : null}
        </footer>
      ) : null}

      {/* ⭐ ТОСТ ПЕРЕХОДА СТОИТ ЗДЕСЬ, А НЕ НА ЭКРАНЕ, ПОТОМУ ЧТО ЭКРАНА УЖЕ НЕТ. Он объявляет
          МЕСТО, КУДА ИДУТ (`→ the second pass`, `s855.d1`), и печатать его на уходящем шаге
          значило бы показать на один кадр — то есть не показать. Копи при этом остаётся у шага:
          строку приносит `announce`, оболочка не знает ни одного слова шага 2.
          ⚠️ КЛАСС ТОЖЕ ОСТАЁТСЯ ШАГА 2 (`.s2-toast`, байт борда `Stage Gate v5`). Второй порт
          тех же объявлений под именем хрома был бы дрейфом с первого же переиздания борда: у
          тоста один носитель на удаление и на переход, потому что у борда он один и тот же узел.
          Место узла (оболочка вместо экрана) — строка в `WIZARD-CANON.md`. */}
      {toast === null ? null : (
        <div data-testid="wizard-toast" className="s2-toast on">{toast}</div>
      )}
      {debug ? <DebugExport session={session} /> : null}
    </div>
  );
}
